import { formatCompactDateTime, formatShortDate } from '@/utils/dateFormatters'
import dayjs from 'dayjs'

export type GroupableThread = {
  id: string
  title: string
  message_count: number
  updated_at: string
  preview?: string | null
}

// Section order; 'pinned' is reserved for a future pinning feature and is never
// populated today (no thread carries a pinned flag yet).
export type SectionKey =
  | 'pinned'
  | 'today'
  | 'yesterday'
  | 'lastWeek'
  | 'earlier'

export type ThreadSection = {
  key: SectionKey
  threads: GroupableThread[]
}

const ORDER: SectionKey[] = [
  'pinned',
  'today',
  'yesterday',
  'lastWeek',
  'earlier',
]

function bucketFor(
  updatedAt: string,
  now: Date,
): Exclude<SectionKey, 'pinned'> {
  const startOfToday = dayjs(now).startOf('day')
  const updated = dayjs(updatedAt)
  const dayDiff = startOfToday.diff(updated.startOf('day'), 'day')
  if (dayDiff <= 0) return 'today'
  if (dayDiff === 1) return 'yesterday'
  if (dayDiff <= 7) return 'lastWeek'
  return 'earlier'
}

export function groupThreads(
  threads: GroupableThread[],
  now: Date = new Date(),
): ThreadSection[] {
  const buckets = new Map<SectionKey, GroupableThread[]>()
  for (const t of threads) {
    const key = bucketFor(t.updated_at, now)
    const arr = buckets.get(key) ?? []
    arr.push(t)
    buckets.set(key, arr)
  }
  const sections: ThreadSection[] = []
  for (const key of ORDER) {
    const arr = buckets.get(key)
    if (!arr || arr.length === 0) continue
    arr.sort(
      (a, b) => dayjs(b.updated_at).valueOf() - dayjs(a.updated_at).valueOf(),
    )
    sections.push({ key, threads: arr })
  }
  return sections
}

// Row timestamp display: a compact day+time for recent rows, an absolute
// month/day for older rows. Uses the shared Crossroads date formatters
// (`@/utils/dateFormatters`) per project convention — S&D's per-bucket
// TIME/WEEKDAY_TIME/MONTH_DAY formatters have no direct Crossroads equivalent.
export function formatRowTimestamp(
  updatedAt: string,
  now: Date = new Date(),
): string {
  const bucket = bucketFor(updatedAt, now)
  if (bucket === 'earlier') return formatShortDate(updatedAt) ?? ''
  return formatCompactDateTime(updatedAt)
}
